import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Divider,
  FormControl,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Theme,
  Typography,
  autocompleteClasses,
  styled,
  useMediaQuery,
} from "@mui/material";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { FC } from "react";
import { ExportDeclarationsQuery } from "../../../graphql/types";
import { getCurrentUtcDate } from "../../../lib/Util";
import { useBackfillStore } from "../../../store/backfillsStore";
import { OptionalRange } from "../../../types";
import { DataLoader, LoadingContainer, SuccessContainer } from "../../DataLoader";
import CalendarAndField from "../../Date/CalendarAndField";
import type { DateRange } from "@mui/x-date-pickers-pro";
import DateRangePicker from "../../Date/DateRangePicker";
import { ProgressBarTitle } from "../ProgressBarTitle";

const AutocompleteStyled = styled(Autocomplete)({
  [`& .${autocompleteClasses.input}`]: {
    fontSize: "14px",
  },
  height: "32px",
  minWidth: "300px",
});

const IconInfoStyled = styled(InfoIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.surfaceLight30.main,
}));

interface ISelectUpload {
  title: string;
  completedPercentage: number;
  exportListData?: ExportDeclarationsQuery;
  exportsLoading: boolean;
}

const DatePicker = () => {
  const date = useBackfillStore((state) => state.date);
  const updateDate = useBackfillStore((state) => state.updateDate);
  const handleChangeDate = ({ end, start }: OptionalRange<Date>) => {
    updateDate({ end, start });
  };
  return (
    <DateRangePicker
      disableHighlightToday
      start={date.start}
      end={date.end}
      defaultEnd={getCurrentUtcDate()}
      defaultStart={getCurrentUtcDate()}
      disableFuture
      calendars={1}
      onChange={(value: DateRange<Date>) => {
        handleChangeDate({ start: value[0], end: value[1] });
      }}
      customHelper={
        <>
          <IconInfoStyled />
          {"The dates are in UTC timezone"}
        </>
      }
    />
  );
};

const SelectUpload: FC<ISelectUpload> = ({ title, completedPercentage, exportListData, exportsLoading }) => {
  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const updateUploadName = useBackfillStore((state) => state.updateUploadName);
  const uploadName = useBackfillStore((state) => state.uploadName);

  const handleSelect = (value: string) => {
    updateUploadName(value);
  };

  return (
    <Stack>
      <ProgressBarTitle title={title} completedPercentage={completedPercentage} />
      <Grid container spacing={isLargeScreen ? 0 : 10} columns={12} sx={{ mt: isLargeScreen ? 2 : 0 }}>
        <Grid size={isLargeScreen ? 6 : 12} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="heading.six" align="center" sx={{ m: 2, justifyContent: "center" }}>
            Report Date
          </Typography>
          {isLargeScreen ? (
            <>
              <CalendarAndField />
              <Grid container sx={{ alignItems: "flex-end" }}>
                <Grid>
                  <IconInfoStyled />
                </Grid>
                <Grid>
                  <Typography variant="body.small.regular">The dates are in UTC timezone </Typography>
                </Grid>
              </Grid>
            </>
          ) : (
            <DatePicker />
          )}
        </Grid>

        {isLargeScreen ? (
          <Grid size={1} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Divider orientation="vertical" />
          </Grid>
        ) : null}

        <Grid
          size={isLargeScreen ? 5 : 12}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: isLargeScreen ? "flex-start" : "center",
          }}
        >
          <Typography variant="heading.six">Uploads</Typography>
          <DataLoader loading={exportsLoading} success={!exportsLoading}>
            <LoadingContainer>
              <Skeleton variant="text">
                <AutocompleteStyled
                  options={[]}
                  renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} variant="standard" />}
                />
              </Skeleton>
            </LoadingContainer>
            <SuccessContainer>
              <FormControl sx={{ m: 1, minWidth: 120 }} data-testid="select-upload">
                <AutocompleteStyled
                  disableClearable={true}
                  options={exportListData?.export_declarations?.edges.map((edge) => edge.node.name) || []}
                  onChange={(_, val) => {
                    if (typeof val === "string") handleSelect(val);
                  }}
                  value={uploadName}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField data-cy="menu-item" {...params} variant="standard" />
                  )}
                />
              </FormControl>
            </SuccessContainer>
          </DataLoader>
        </Grid>
      </Grid>
    </Stack>
  );
};

export { SelectUpload };
